import { useState, useEffect, useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import api from '../services/api';

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

const ReviewCard = ({ review }) => (
  <div
    className="flex-shrink-0 w-[300px] sm:w-[340px] bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl p-5 hover:bg-white/12 transition-colors"
    data-testid={`featured-review-${review.id}`}
  >
    <div className="flex items-center gap-1 mb-3">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={`star-${i}`} className={`w-3.5 h-3.5 ${review.rating >= i ? 'fill-gold text-gold' : 'text-white/20'}`} />
      ))}
    </div>
    <Quote className="w-5 h-5 text-gold/30 mb-1" />
    <p className="text-white/80 text-sm leading-relaxed mb-3 line-clamp-4">{review.comment}</p>
    <div className="flex items-center justify-between">
      <p className="text-white/60 text-xs font-medium">{review.name}</p>
      {review.location && (
        <p className="text-gold/50 text-xs flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {review.location}
        </p>
      )}
    </div>
  </div>
);

const LandingReviews = () => {
  const [reviews, setReviews] = useState([]);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // Fetch highest-rated website reviews
        const res = await api.get('/website-reviews?limit=12');
        if (res.data.success && res.data.reviews.length > 0) {
          // Sort by rating desc, then by date desc
          const sorted = [...res.data.reviews].sort((a, b) => b.rating - a.rating || new Date(b.created_at) - new Date(a.created_at));
          setReviews(sorted);
          return;
        }
      } catch {
        // fallback silently
      }

      // Fallback: try trip-specific reviews
      try {
        const res = await api.get('/reviews/public?limit=6');
        if (res.data.success && res.data.reviews.length > 0) {
          setReviews(res.data.reviews.map(r => ({
            id: r.id,
            name: r.user_name || 'Traveler',
            location: r.destination_city ? `${r.destination_city}, ${r.destination_country || ''}` : '',
            rating: r.rating,
            comment: r.text,
          })));
        }
      } catch {
        // silent
      }
    };
    fetchReviews();
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 360, behavior: 'smooth' });
    }
  };

  if (reviews.length === 0) return null;

  return (
    <motion.section
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-60px' }}
      className="py-16 px-4 sm:px-6 relative bg-gradient-to-b from-ocean-light/80 to-ocean/90"
      data-testid="landing-reviews-section"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-8">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2">What Our Users Say</h2>
          <p className="text-white/60 text-sm">Real experiences from our community</p>
        </motion.div>

        {/* Carousel */}
        <div className="relative">
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition-colors hidden sm:flex"
            data-testid="reviews-carousel-left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory px-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {reviews.map((review) => (
              <div key={review.id} className="snap-start">
                <ReviewCard review={review} />
              </div>
            ))}
          </div>

          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition-colors hidden sm:flex"
            data-testid="reviews-carousel-right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <motion.div variants={fadeUp} className="text-center mt-8">
          <Button
            variant="outline"
            onClick={() => navigate('/reviews')}
            className="text-white/70 border-white/20 hover:bg-white/10 hover:text-white rounded-full px-6 text-sm"
            data-testid="view-all-reviews-btn"
          >
            View All Reviews
          </Button>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default LandingReviews;
